import { faChartWaterfall } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FC, useContext, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartGroupByMonthYear, SiteEventType, Species } from "../../../api/dtos/dtos";
import MapContext from "../../../contexts/data/MapContext";
import { SpeciesContext } from "../../../contexts/data/SpeciesContext";

interface IAreaChartProps {
    date: string, 
    sightingCount: number, 
    damageCount: number, 
    controlCount: number, 
    wildDogCustomCount: number
}

interface IAreaGraphCardProps {
    title: string,
    siteeventtypes: SiteEventType[],
    sightingChartData?: ChartGroupByMonthYear[],
    damageChartData?: ChartGroupByMonthYear[],
    controlChartData?: ChartGroupByMonthYear[],
    wildDogCustomData?: ChartGroupByMonthYear[],
}

const AreaGraphCard: FC<IAreaGraphCardProps> = ({
    title,
    siteeventtypes,
    sightingChartData,
    damageChartData,
    controlChartData,
    wildDogCustomData
}) => {
    //
    // -- States --
    const [formattedChartData, setFormattedChartData] = useState<IAreaChartProps[] | undefined>(undefined);
    const [hiddenData, setHiddenData] = useState<Record<string, boolean>>({});

    //
    // -- Contexts --
    const { filter } = useContext(MapContext);
    const { species: {species} } = useContext(SpeciesContext);

    useEffect(() => {
        if ((sightingChartData && sightingChartData?.length > 0) || 
            (damageChartData && damageChartData?.length > 0) ||
            (controlChartData && controlChartData?.length > 0) ||
            (wildDogCustomData && wildDogCustomData?.length > 0)) {
    
            let formattedSightingData = sightingChartData?.map(item => ({
                rawDate: item.date,
                sightingCount: item.count,
                damageCount: 0,
                controlCount: 0,
                wildDogCustomCount: 0
            })) || [];
    
            let formattedDamageData = damageChartData?.map(item => ({
                rawDate: item.date,
                sightingCount: 0,
                damageCount: item.count,
                controlCount: 0,
                wildDogCustomCount: 0
            })) || [];
    
            let formattedControlData = controlChartData?.map(item => ({
                rawDate: item.date,
                sightingCount: 0,
                damageCount: 0,
                controlCount: item.count,
                wildDogCustomCount: 0
            })) || [];
    
            let formattedWildDogCustomData = wildDogCustomData?.map(item => ({
                rawDate: item.date,
                sightingCount: 0,
                damageCount: 0,
                controlCount: 0,
                wildDogCustomCount: item.count
            })) || [];
    
            // Show and hide data
            formattedSightingData = hiddenData["Sighting Records"] ? [] : formattedSightingData;
            formattedDamageData = hiddenData["Damage Records"] ? [] : formattedDamageData;
            formattedControlData = hiddenData["Control Records"] ? [] : formattedControlData;
            formattedWildDogCustomData = hiddenData["Number of wild dogs recorded"] ? [] : formattedWildDogCustomData;


            // Merge all chart data
            const mergedData = [...formattedSightingData, ...formattedDamageData, ...formattedControlData, ...formattedWildDogCustomData].reduce(
                (acc: any[], current: any) => {
                    const existing = acc.find(item => item.rawDate === current.rawDate);
                    if (existing) {
                        existing.sightingCount += current.sightingCount;
                        existing.damageCount += current.damageCount;
                        existing.controlCount += current.controlCount;
                        existing.wildDogCustomCount += current.wildDogCustomCount;
                    } else {
                        acc.push(current);
                    }
                    return acc;
                },
                []
            );
    
            // Sort merged data by raw date (original date string)
            mergedData.sort((a, b) => dayjs(a.rawDate).isBefore(dayjs(b.rawDate)) ? -1 : 1);
    
            // Get the range of months between the min and max date
            const minDate = dayjs(mergedData[0]?.rawDate);
            const maxDate = dayjs(mergedData[mergedData.length - 1]?.rawDate);
    
            // Create an array of all months between minDate and maxDate
            let currentMonth = minDate.startOf('month');
            const allMonths = [];
            while (currentMonth.isBefore(maxDate) || currentMonth.isSame(maxDate)) {
                allMonths.push(currentMonth.format("YYYY-MM"));
                currentMonth = currentMonth.add(1, 'month');
            }
    
            // Fill in missing months with zero counts
            const finalMergedData = allMonths.map(month => {
                const existingData = mergedData.find(item => dayjs(item.rawDate).format("YYYY-MM") === month);
                return existingData || {
                    rawDate: dayjs(month).toISOString(),
                    sightingCount: 0,
                    damageCount: 0,
                    controlCount: 0,
                    wildDogCustomCount: 0
                };
            });
    
            // Format date after sorting
            const finalFormattedData: IAreaChartProps[] = finalMergedData.map(item => ({
                date: dayjs(item.rawDate).format("MMM YYYY"), // Format after sorting
                sightingCount: item.sightingCount,
                damageCount: item.damageCount,
                controlCount: item.controlCount,
                wildDogCustomCount: item.wildDogCustomCount
            }));
    
            // Set the formatted data
            setFormattedChartData(finalFormattedData);
        }
    }, [sightingChartData, damageChartData, controlChartData, wildDogCustomData, hiddenData]);

    const handleOnClick = (o: any) => {
        const { value } = o;

        setHiddenData(current => ({
            ...current,
            [value]: !current[value]
        }));
    }

    return (
        <Card 
            variant='outlined'
            sx={{
                marginY: '15px'
            }}
        >
            <CardContent>
                <Grid container spacing={4} alignItems={"center"}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems={"center"}>
                            <FontAwesomeIcon icon={faChartWaterfall} style={{ fontSize: '1.2rem', paddingRight: "5px" }} />
                            <Typography paddingLeft={"5px"} fontSize={24}>
                                {title}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{padding: "10px"}} />

                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                        <AreaChart 
                            width={1000} 
                            height={250} 
                            data={formattedChartData ?? []}
                            margin={{ 
                                top: 10, 
                                right: 30, 
                                left: 0, 
                                bottom: 0 
                            }}
                            accessibilityLayer
                        >
                            <defs>
                                <linearGradient id="sightingColour" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="1%" stopColor="#4cc61b" stopOpacity={0.75}/>
                                    <stop offset="100%" stopColor="#4cc61b" stopOpacity={0.05}/>
                                </linearGradient>
                                <linearGradient id="damageColour" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="1%" stopColor="#f23e3e" stopOpacity={0.75}/>
                                    <stop offset="100%" stopColor="#f23e3e" stopOpacity={0.05}/>
                                </linearGradient>
                                <linearGradient id="controlColour" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="1%" stopColor="#0300e0" stopOpacity={0.75}/>
                                    <stop offset="100%" stopColor="#0300e0" stopOpacity={0.05}/>
                                </linearGradient>
                                <linearGradient id="wildDogCustomColour" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="1%" stopColor="#3f9162" stopOpacity={0.75}/>
                                    <stop offset="100%" stopColor="#3f9162" stopOpacity={0.05}/>
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="date" />
                            <YAxis />
                            <CartesianGrid strokeDasharray="3 3" />
                            <Legend
                                iconType="diamond"
                                onClick={handleOnClick}
                                formatter={(value, _entry, _index) => (
                                    <span 
                                        style={{ 
                                            textDecoration: hiddenData[value] ? 'line-through' : 'none'
                                        }}
                                    >
                                        {value}
                                    </span>
                                )}
                            />
                            <Tooltip />
                            {(filter.recordType.sighting && siteeventtypes?.includes(SiteEventType.Sighting) && sightingChartData) && (
                                <Area 
                                    name="Sighting Records" 
                                    type="monotone" 
                                    dataKey="sightingCount" 
                                    stroke="#4cc61b" 
                                    strokeOpacity={1} 
                                    fillOpacity={1} 
                                    fill="url(#sightingColour)" 
                                    />
                            )}
                            {(filter.recordType.damage && siteeventtypes?.includes(SiteEventType.Damage) && damageChartData) && (
                                <Area 
                                    name="Damage Records" 
                                    type="monotone" 
                                    dataKey="damageCount" 
                                    stroke="#f23e3e" 
                                    strokeOpacity={1} 
                                    fillOpacity={1} 
                                    fill="url(#damageColour)" 
                                />
                            )}
                            {(filter.recordType.control && siteeventtypes?.includes(SiteEventType.Control) && controlChartData) && (
                                <Area 
                                    name="Control Records" 
                                    type="monotone" 
                                    dataKey="controlCount" 
                                    stroke="#0300e0" 
                                    strokeOpacity={1} 
                                    fillOpacity={1}  
                                    fill="url(#controlColour)" 
                                />
                            )}
                            {(!siteeventtypes.includes(SiteEventType.Control) && species === Species.WildDog) && (
                                <Area 
                                    name="Number of wild dogs recorded" 
                                    type="monotone" 
                                    dataKey="wildDogCustomCount" 
                                    stroke="#3f9162" 
                                    strokeOpacity={1} 
                                    fillOpacity={1} 
                                    fill="url(#wildDogCustomColour)" 
                                />
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    )
}

export default AreaGraphCard